import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import useMeasure from 'react-use-measure';
import { AnimatedLogo } from '@/embedded/chat/components/animated-logo';
import { cn } from '@/utils/cn';
import { createPortal } from 'react-dom';

const SEARCH_DEBOUNCE_MS = 180;
const contentTransition = {
  duration: 0.22,
  ease: [0.22, 1, 0.36, 1],
} as const;
type SearchStatus = 'idle' | 'loading' | 'ready' | 'error';
type SearchContentState = 'idle' | 'loading' | 'results' | 'empty' | 'error';

export type WebsiteConversationSearchResult = {
  id: string;
  conversationId: string;
  conversationTitle: string;
  messageId: string;
  role: string;
  createdAt: number;
  snippet: string;
  score: number;
};

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim().length > 0) {
    return error;
  }
  return 'Search failed. Please try again.';
}

function normalizeUnixTimestamp(timestamp: number): number {
  if (!Number.isFinite(timestamp)) {
    return Date.now();
  }
  return timestamp < 100_000_000_000 ? timestamp * 1000 : timestamp;
}

function formatSearchTime(timestamp: number): string {
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  const date = new Date(normalizeUnixTimestamp(timestamp));
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function renderSnippet(snippet: string) {
  const parts = snippet.split(/«(.*?)»/g);
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <mark
        key={`${index}-${part}`}
        className="rounded bg-primary/15 px-0.5 text-foreground"
      >
        {part}
      </mark>
    ) : (
      part
    )
  );
}

export function ConversationSearchPanel({
  open,
  requestToken,
  onClose,
  onSearch,
  onOpenResult,
}: {
  open: boolean;
  requestToken: number;
  onClose: () => void;
  onSearch: (query: string) => Promise<WebsiteConversationSearchResult[]>;
  onOpenResult: (result: WebsiteConversationSearchResult) => Promise<void>;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WebsiteConversationSearchResult[]>([]);
  const [status, setStatus] = useState<SearchStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRequestRef = useRef(0);
  const [contentRef, contentBounds] = useMeasure();

  const trimmedQuery = useMemo(() => query.trim(), [query]);
  const contentState = useMemo<SearchContentState>(() => {
    if (trimmedQuery.length === 0) {
      return 'idle';
    }
    if (status === 'loading') {
      return 'loading';
    }
    if (status === 'error') {
      return 'error';
    }
    if (results.length === 0) {
      return 'empty';
    }
    return 'results';
  }, [results.length, status, trimmedQuery.length]);
  const animatedContentHeight = contentBounds.height || 168;

  const runSearch = useCallback(
    async (searchQuery: string) => {
      const requestId = activeRequestRef.current + 1;
      activeRequestRef.current = requestId;
      setStatus('loading');
      setError(null);

      try {
        const nextResults = await onSearch(searchQuery);
        if (activeRequestRef.current !== requestId) {
          return;
        }
        setResults(nextResults ?? []);
        setStatus('ready');
      } catch (searchError) {
        if (activeRequestRef.current !== requestId) {
          return;
        }
        setResults([]);
        setStatus('error');
        setError(getErrorMessage(searchError));
      }
    },
    [onSearch]
  );

  const handleOpenResult = useCallback(
    async (result: WebsiteConversationSearchResult) => {
      setError(null);
      setSelectedResultId(result.id);

      try {
        await onOpenResult(result);
        onClose();
      } catch (openError) {
        setError(getErrorMessage(openError));
      } finally {
        setSelectedResultId(current =>
          current === result.id ? null : current
        );
      }
    },
    [onClose, onOpenResult]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setSelectedResultId(null);
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [open, requestToken]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!trimmedQuery) {
      activeRequestRef.current += 1;
      setResults([]);
      setStatus('idle');
      setError(null);
      setSelectedResultId(null);
      return;
    }

    setStatus('loading');
    setError(null);
    setSelectedResultId(null);
    const timer = window.setTimeout(() => {
      void runSearch(trimmedQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [open, runSearch, trimmedQuery]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onWindowKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onWindowKeyDown);
    return () => window.removeEventListener('keydown', onWindowKeyDown);
  }, [onClose, open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeInOut' }}
            className="absolute size-full left-0 top-0 z-[80] bg-neutral-950/5"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0.1, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25, ease: [0.49, 0.43, 0.09, 1] }}
            className={cn(
              'fixed top-14 left-1/2 z-[81] flex w-[calc(100vw-2rem)] max-w-[680px] -translate-x-1/2 flex-col overflow-hidden rounded-[24px] outline-none md:top-24',
              'bg-background/80 dark:bg-neutral-800/80',
              'shadow-[0_24px_80px_rgba(0,0,0,0.18)] dark:shadow-[0_24px_80px_rgba(0,0,0,0.38)]',
              'backdrop-blur-lg origin-bottom'
            )}
          >
            <div className="border-b border-0.5px border-black/4 dark:border-white/5 px-4 py-1">
              <div className="flex items-center gap-3">
                <Search
                  className="size-4 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Search conversations"
                  className="h-12 w-full border-0 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                  aria-label="Search conversations"
                />

                <button
                  type="button"
                  aria-label="Close search"
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-[8px] text-muted-foreground transition-colors duration-200',
                    'hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground'
                  )}
                  onClick={onClose}
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>

            {error && status !== 'error' ? (
              <p className="px-4 pb-1 text-xs text-rose-600 dark:text-rose-300">
                {error}
              </p>
            ) : null}

            <div className="overflow-hidden px-2 pb-3 pt-1">
              <motion.div
                initial={false}
                animate={{ height: animatedContentHeight }}
                transition={contentTransition}
                className="overflow-hidden"
              >
                <div ref={contentRef}>
                  <AnimatePresence initial={false} mode="wait">
                    <motion.div
                      key={contentState}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={contentTransition}
                      className="overflow-hidden"
                    >
                      {contentState === 'idle' ? (
                        <div className="flex min-h-[168px] items-center justify-center px-6 text-sm text-muted-foreground">
                          Type to search
                        </div>
                      ) : contentState === 'loading' ? (
                        <div className="flex min-h-[168px] flex-col items-center justify-center gap-3 px-6 py-10">
                          <AnimatedLogo
                            loop
                            className="h-[30px] text-foreground/18"
                            aria-label="Searching conversations"
                          />
                          <p className="text-sm text-muted-foreground">
                            Searching...
                          </p>
                        </div>
                      ) : contentState === 'error' ? (
                        <div className="flex min-h-[168px] flex-col items-center justify-center gap-4 px-6 text-center">
                          <p className="text-sm text-rose-600 dark:text-rose-300">
                            {error ?? 'Search failed.'}
                          </p>
                          <button
                            type="button"
                            className="inline-flex h-9 items-center justify-center rounded-full bg-muted px-4 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-muted/80"
                            onClick={() => {
                              if (!trimmedQuery) {
                                return;
                              }
                              void runSearch(trimmedQuery);
                            }}
                          >
                            Try again
                          </button>
                        </div>
                      ) : contentState === 'empty' ? (
                        <div className="flex min-h-[168px] items-center justify-center px-6 text-center text-sm text-muted-foreground">
                          No results for “{trimmedQuery}”
                        </div>
                      ) : (
                        <div className="max-h-[min(56vh,520px)] overflow-y-auto px-1 py-1">
                          <div className="flex flex-col gap-1">
                            {results.map(result => {
                              const isSelected = selectedResultId === result.id;

                              return (
                                <button
                                  key={result.id}
                                  type="button"
                                  className={cn(
                                    'w-full rounded-2xl px-3 py-2.5 text-left transition-colors duration-150',
                                    isSelected &&
                                      'pointer-events-none opacity-55',
                                    'hover:bg-black/5 dark:hover:bg-white/5'
                                  )}
                                  disabled={isSelected}
                                  onClick={() => {
                                    void handleOpenResult(result);
                                  }}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                      <div className="truncate text-sm font-medium text-foreground">
                                        {result.conversationTitle ||
                                          'Untitled conversation'}
                                      </div>
                                      <div className="mt-0.5 truncate text-sm text-muted-foreground">
                                        {result.snippet
                                          ? renderSnippet(result.snippet)
                                          : 'No preview available'}
                                      </div>
                                    </div>

                                    <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
                                      <span className="text-[11px] text-muted-foreground">
                                        {formatSearchTime(result.createdAt)}
                                      </span>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
